/**
 * CR-7106: URL persistence for the deliveries list's applied filter state.
 *
 * A page refresh (still the habitual workaround for a stale row) used to wipe
 * every filter: CR-4580 removed the old sessionStorage persistence and the
 * mount effect stripped even the deep-link params after consuming them.
 * CR-5769 later reinstated persistence for the PO Lines list by KEEPING
 * filter params in the URL ("so filter persists after page refresh", same
 * pattern as Quality.tsx). This module extends that direction to the full
 * deliveries filter set: applied filters are serialised into the URL
 * (replace, no history spam) and hydrated back on mount.
 *
 * Namespace rules (adversarial-review round 1):
 * - Everything WRITTEN is `dlv_`-prefixed. The /purchasing URL is shared by
 *   every purchasing tab and unprefixed names collide — a bare `supplier`
 *   param, for instance, is applied by PO Lines and PO Headers on their own
 *   mounts.
 * - The historical deep-link names (grnno / ponumber / linenumber /
 *   appealstatus, plus the older search / grnstatus aliases) are READ-ONLY
 *   aliases: consumed on mount so old links keep working, then removed by
 *   the first rewrite so the URL converges on the dlv_ namespace. Removing
 *   `grnno` in particular is load-bearing: DeliveryTab's auto-open ref
 *   consumes it once per mount, so leaving it in the URL would re-open the
 *   Delivery Details modal on every reload.
 * - The rewrite edits a copy of the CURRENT params, deleting only our own
 *   names, so sibling tabs' persisted params (po / line / poFilter /
 *   status_tab / …) survive a visit to the deliveries tab.
 *
 * @link DeliveryTab for the consuming state; usePOLinesState for the
 * CR-5769 precedent.
 */

/** Filter-state key → URL param name. All written params are dlv_-prefixed
 * (see namespace rules above). */
export const DELIVERY_FILTER_PARAMS: ReadonlyArray<readonly [string, string]> = [
  ['grnNumber', 'dlv_grn'],
  ['poNumber', 'dlv_po'],
  ['lineNumber', 'dlv_line'],
  ['appealStatus', 'dlv_appeal_status'],
  ['supplier', 'dlv_supplier'],
  ['partNumber', 'dlv_part'],
  ['inspection_status', 'dlv_inspection'],
  ['dateFrom', 'dlv_date_from'],
  ['dateTo', 'dlv_date_to'],
  ['oddFrom', 'dlv_odd_from'],
  ['oddTo', 'dlv_odd_to'],
  ['site', 'dlv_site'],
  ['warehouse', 'dlv_warehouse'],
  ['supplierGroup', 'dlv_supplier_group'],
  ['commodityGroup', 'dlv_commodity_group'],
  ['commodityType', 'dlv_commodity_type'],
  ['appealFilter', 'dlv_appeal_filter'],
  ['poLineStatus', 'dlv_po_line_status'],
  // filters.grnStatus feeds the grid's ON-TIME status lookup
  // (deliveriesData maps it to on_time_status; workflow_status is fed by
  // appealStatus) — the param name says what it filters.
  ['grnStatus', 'dlv_on_time'],
  ['strategic', 'dlv_strategic'],
  ['supplierCode', 'dlv_supplier_code'],
  ['customerCountry', 'dlv_customer_country'],
  ['division', 'dlv_division'],
  ['supplierCountry', 'dlv_supplier_country'],
  ['supplierType', 'dlv_supplier_type'],
  // CR-7273: dlv_order_type dropped with the Order Type control (dormant column).
  ['buyerCode', 'dlv_buyer_code'],
  ['strategicOnly', 'dlv_strategic_only'],
];

/** Legacy deep-link param → filter-state key. Read-only aliases: consumed on
 * mount, removed by the first rewrite. Order matters — an explicit dlv_
 * param always wins, and within aliases the first hit wins. */
const LEGACY_ALIASES: ReadonlyArray<readonly [string, string]> = [
  ['grnno', 'grnNumber'],
  ['search', 'grnNumber'],
  ['ponumber', 'poNumber'],
  ['linenumber', 'lineNumber'],
  ['appealstatus', 'appealStatus'],
  ['grnstatus', 'appealStatus'],
];

const EXTRA_PARAMS = ['dlv_tab', 'dlv_sort', 'dlv_dir', 'dlv_page_size'] as const;

/** Every param name this module owns — deleted from the URL on each rewrite
 * before our current values are set, so foreign params are never touched. */
export const OWN_URL_PARAMS: ReadonlyArray<string> = [
  ...DELIVERY_FILTER_PARAMS.map(([, param]) => param),
  ...LEGACY_ALIASES.map(([param]) => param),
  ...EXTRA_PARAMS,
];

/**
 * True for a value that is really a filter, not a stringified nullish.
 *
 * `String(undefined)` is `"undefined"` — truthy, so it survives a plain
 * `if (value)` guard, persists into the URL and is then sent to the API,
 * where a choice filter rejects it with "Select a valid choice. That choice
 * is not one of the available choices." and the whole list renders as an
 * error. Guarding both directions means a URL that already carries the junk
 * (bookmarked or shared before the fix) drops it on load instead of failing
 * forever, and no future mapping slip can write it back.
 *
 * @param value - the raw param / filter value
 * @returns true when the value should be treated as an applied filter
 */
function isRealValue(value: string | null | undefined): value is string {
  return !!value && value !== 'undefined' && value !== 'null' && value !== 'NaN';
}

export interface DeliveryUrlState {
  /** Filter-state keys (not param names) → values found in the URL. */
  filters: Record<string, string>;
  activeTab: string | null;
  sortField: string | null;
  sortDirection: 'asc' | 'desc' | null;
  pageSize: number | null;
  /** True when any filter param was present (drives auto-opening the panel). */
  hasFilterParams: boolean;
}

/**
 * Read the persisted/deep-linked delivery filter state out of the URL.
 *
 * @param searchParams - the current URLSearchParams
 * @returns the hydratable state; empty-object filters when none present
 */
export function readDeliveryUrlState(searchParams: URLSearchParams): DeliveryUrlState {
  const filters: Record<string, string> = {};
  let hasFilterParams = false;
  for (const [key, param] of DELIVERY_FILTER_PARAMS) {
    const value = searchParams.get(param);
    if (isRealValue(value)) {
      filters[key] = value;
      hasFilterParams = true;
    }
  }
  for (const [param, key] of LEGACY_ALIASES) {
    const value = searchParams.get(param);
    if (isRealValue(value) && !filters[key]) {
      filters[key] = value;
      hasFilterParams = true;
    }
  }
  const sortDir = searchParams.get('dlv_dir');
  const pageSizeRaw = Number(searchParams.get('dlv_page_size'));
  return {
    filters,
    activeTab: searchParams.get('dlv_tab'),
    sortField: searchParams.get('dlv_sort'),
    sortDirection: sortDir === 'asc' || sortDir === 'desc' ? sortDir : null,
    pageSize: Number.isFinite(pageSizeRaw) && pageSizeRaw > 0 ? pageSizeRaw : null,
    hasFilterParams,
  };
}

/**
 * Produce the next URL params: a copy of `current` with our own params
 * removed and the applied state re-serialised. Foreign params (other tabs'
 * deep links and persisted filters) pass through untouched; defaults stay
 * OUT of the URL so an unfiltered visit keeps a clean address bar.
 *
 * @param current - the current URLSearchParams (copied, not mutated)
 * @param filters - the APPLIED filter state (not pending inputs)
 * @param extras - quick-tab / sort / page-size state
 * @returns the params object to hand to setSearchParams
 */
export function applyDeliveryUrlParams(
  current: URLSearchParams,
  filters: Record<string, string>,
  extras: { activeTab: string; sortField: string; sortDirection: string; pageSize: number },
): URLSearchParams {
  const next = new URLSearchParams(current);
  for (const param of OWN_URL_PARAMS) next.delete(param);
  for (const [key, param] of DELIVERY_FILTER_PARAMS) {
    const value = filters[key];
    if (isRealValue(value)) next.set(param, value);
  }
  if (extras.activeTab && extras.activeTab !== 'all') next.set('dlv_tab', extras.activeTab);
  if (extras.sortField !== 'receipt_date') next.set('dlv_sort', extras.sortField);
  if (extras.sortDirection !== 'desc') next.set('dlv_dir', extras.sortDirection);
  if (extras.pageSize !== 50) next.set('dlv_page_size', String(extras.pageSize));
  return next;
}
